import { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

interface CharterChatInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop?: () => void
  disabled?: boolean
  isStreaming?: boolean
  placeholder?: string
}

/**
 * Chat input that mirrors the platform's player-side ChatPill so the charter
 * chat is visually identical to the in-game advisor chat.
 */
export function CharterChatInput({
  value,
  onChange,
  onSend,
  onStop,
  disabled = false,
  isStreaming = false,
  placeholder,
}: CharterChatInputProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!value.trim() || disabled || isStreaming) return
    onSend()
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!value.trim() || disabled || isStreaming) return
      onSend()
      // Enter is the primary send path; without this the caret is lost after
      // every message and the user has to click back into the box.
      inputRef.current?.focus()
    }
  }

  return (
    <form onSubmit={handleSubmit} className='flex flex-1 gap-2'>
      <div className='relative min-h-[50px] flex items-start flex-1 px-3 py-2 border border-[#CBD5E1] bg-[#E6ECF2] rounded-2xl'>
        <Textarea
          ref={inputRef}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            placeholder ??
            'Type your message… (Enter to send, Shift+Enter for new line)'
          }
          rows={1}
          className='flex-1 min-w-0 min-h-[36px] max-h-40 resize-none overflow-y-auto text-sm leading-[36px] bg-transparent border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0'
        />
        {isStreaming && onStop ? (
          <Button
            type='button'
            onClick={onStop}
            className='bg-transparent shadow-none hover:bg-transparent p-1'
          >
            <img src='/icons/stop-circle.svg' alt='Stop' className='h-7 w-7' />
          </Button>
        ) : (
          <Button
            type='submit'
            disabled={!value.trim() || disabled || isStreaming}
            className='bg-transparent shadow-none hover:bg-transparent p-1'
          >
            <img
              src={
                !value.trim()
                  ? '/icons/arrow-up-circle-white.svg'
                  : '/icons/arrow-up-circle.svg'
              }
              alt='Send'
              className='h-7 w-7'
            />
          </Button>
        )}
      </div>
    </form>
  )
}
